import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignAPI } from '../../services/api';
import { toast } from 'react-toastify';
import AdminDashboard from './AdminDashboard';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import {
    DocumentTextIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    UserGroupIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    ChartBarIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    CurrencyRupeeIcon,
    LightBulbIcon,
    ShieldCheckIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    CalendarDaysIcon,
    BoltIcon,
    SignalIcon,
    ChartPieIcon,
    CreditCardIcon,
    SparklesIcon,
    ChevronDownIcon,
    FunnelIcon,
} from '@heroicons/react/24/outline';

const Dashboard = () => {
    const { user, isAdmin } = useAuth();

    // All hooks must be called before any conditional returns
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCampaigns, setSelectedCampaigns] = useState(['all']);
    const [campaignFilterData, setCampaignFilterData] = useState([]);
    const [allCampaigns, setAllCampaigns] = useState([]);
    const [selectedVoiceCampaign, setSelectedVoiceCampaign] = useState('all');
    const [voiceCampaignStats, setVoiceCampaignStats] = useState(null);

    // All hooks (useCallback) must be defined before any conditional return
    const fetchDashboardStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await campaignAPI.getDashboardStats();
            setStats(response.data.data);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to load dashboard statistics';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchVoiceCampaignStats = useCallback(async () => {
        if (selectedVoiceCampaign === 'all') {
            setVoiceCampaignStats(stats?.listeningStatistics || null);
            return;
        }

        try {
            const params = { campaignId: selectedVoiceCampaign };
            const response = await campaignAPI.getDashboardStats(params);
            setVoiceCampaignStats(response.data.data.listeningStatistics);
        } catch (error) {
            toast.error('Failed to load voice campaign statistics');
        }
    }, [selectedVoiceCampaign, stats?.listeningStatistics]);

    const fetchCampaignFilterData = useCallback(async () => {
        try {
            // Use lightweight mode to skip expensive CampaignContact queries
            // and fetch all campaigns for the filter dropdown
            const response = await campaignAPI.getCampaigns({ lightweight: 'true', limit: 500 });
            const campaigns = response.data.data.campaigns || [];
            setAllCampaigns(campaigns);

            let filteredCampaigns = campaigns;
            if (!selectedCampaigns.includes('all')) {
                filteredCampaigns = campaigns.filter(campaign =>
                    selectedCampaigns.includes(campaign._id)
                );
            }

            const chartData = filteredCampaigns.map(campaign => {
                const stats = campaign.overallStats || campaign.statistics || {};
                const totalContacts = stats.totalContacts || stats.totalNumbers || 0;
                const delivered = stats.delivered || 0;
                const failed = stats.failed || 0;
                const sent = stats.sent || 0;
                const pending = Math.max(0, totalContacts - delivered - failed - sent);

                return {
                    name: campaign.title.length > 20 ? campaign.title.substring(0, 20) + '...' : campaign.title,
                    fullName: campaign.title,
                    id: campaign._id,
                    type: campaign.type,
                    status: campaign.status,
                    totalContacts,
                    delivered,
                    failed,
                    sent,
                    pending,
                    successRate: totalContacts > 0 ? Math.round((delivered / totalContacts) * 100) : 0
                };
            });

            setCampaignFilterData(chartData);
        } catch (error) {
            // Campaign filter data failed silently
        }
    }, [selectedCampaigns]);

    useEffect(() => {
        if (!isAdmin()) {
            fetchDashboardStats();
            fetchCampaignFilterData();
        }
    }, [isAdmin, fetchDashboardStats, fetchCampaignFilterData]);

    useEffect(() => {
        if (!isAdmin() && selectedCampaigns.length > 0) {
            fetchCampaignFilterData();
        }
    }, [selectedCampaigns, isAdmin, fetchCampaignFilterData]);

    useEffect(() => {
        if (!isAdmin() && stats) {
            fetchVoiceCampaignStats();
        }
    }, [selectedVoiceCampaign, isAdmin, stats, fetchVoiceCampaignStats]);

    // If user is admin, render AdminDashboard instead
    if (isAdmin()) {
        return <AdminDashboard />;
    }

    const campaignTypeIcons = {
        sms: PhoneIcon,
        whatsapp: ChatBubbleLeftRightIcon,
        voice: SpeakerWaveIcon,
        SMS: PhoneIcon,
        Whatsapp: ChatBubbleLeftRightIcon,
        Voice: SpeakerWaveIcon,
    };

    const getInsightIcon = (type) => {
        switch (type) {
            case 'success': return ShieldCheckIcon;
            case 'warning': return ExclamationCircleIcon;
            case 'error': return ExclamationTriangleIcon;
            case 'info': return InformationCircleIcon;
            default: return LightBulbIcon;
        }
    };

    const getInsightColorClass = (type) => {
        switch (type) {
            case 'success': return 'bg-success-50 border-success-200 text-success-800';
            case 'warning': return 'bg-warning-50 border-warning-200 text-warning-800';
            case 'error': return 'bg-error-50 border-error-200 text-error-800';
            case 'info': return 'bg-primary-50 border-primary-200 text-primary-800';
            default: return 'bg-soft-50 border-soft-200 text-soft-800';
        }
    };

    const formatMonth = (yearMonth) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[yearMonth.month - 1]} ${yearMonth.year}`;
    };

    // Custom Tooltip Component for better styling
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 rounded-lg shadow-lg border border-soft-200">
                    <p className="font-semibold text-soft-900 mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between min-w-[180px] mb-1">
                            <span className="flex items-center">
                                <div
                                    className="w-3 h-3 rounded-full mr-2"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-soft-600">{entry.name}:</span>
                            </span>
                            <span className="font-medium text-soft-900">
                                {entry.value?.toLocaleString() || 0}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Monthly Trends Chart Component
    const MonthlyTrendsChart = ({ data, formatMonth }) => {
        // Format data for the chart
        const chartData = data.map(trend => ({
            month: formatMonth(trend._id),
            campaigns: trend.campaignCount,
            totalNumbers: trend.totalNumbers || 0,
            delivered: trend.totalDelivered || 0,
            failed: trend.totalFailed || 0,
            successRate: (trend.totalDelivered + trend.totalFailed + trend.totalSent) > 0 ? Math.round((trend.totalDelivered / (trend.totalDelivered + trend.totalFailed + trend.totalSent)) * 100) : 0
        }));

        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 60,
                    }}
                    barCategoryGap="20%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="rect"
                        formatter={(value) => (
                            <span className="text-sm font-medium text-soft-700">{value}</span>
                        )}
                    />
                    <Bar
                        dataKey="delivered"
                        name="Delivered"
                        fill="#d1fae5"
                        radius={[4, 4, 0, 0]}
                        stroke="#10b981"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="failed"
                        name="Failed"
                        fill="#fee2e2"
                        radius={[4, 4, 0, 0]}
                        stroke="#ef4444"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="campaigns"
                        name="Campaigns"
                        fill="#dbeafe"
                        radius={[4, 4, 0, 0]}
                        stroke="#3b82f6"
                        strokeWidth={2}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // Channel Performance Chart Component
    const ChannelPerformanceChart = ({ data }) => {
        // Format data for the chart
        const chartData = data.map(channel => ({
            channel: channel.channel.toUpperCase(),
            successRate: channel.successRate,
            delivered: channel.delivered,
            sent: channel.sent,
            failed: channel.failed
        }));

        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 20,
                    }}
                    barCategoryGap="30%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="channel"
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                    />
                    <YAxis
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                        dataKey="successRate"
                        name="Success Rate (%)"
                        fill="#d1fae5"
                        radius={[4, 4, 0, 0]}
                        stroke="#10b981"
                        strokeWidth={2}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // Campaign Filter Chart Component
    const CampaignFilterChart = ({ data }) => {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 60,
                    }}
                    barCategoryGap="20%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        vertical={false}
                    />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="rect"
                        formatter={(value) => (
                            <span className="text-sm font-medium text-soft-700">{value}</span>
                        )}
                    />
                    <Bar
                        dataKey="delivered"
                        name="Delivered"
                        fill="#d1fae5"
                        radius={[4, 4, 0, 0]}
                        stroke="#10b981"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="failed"
                        name="Failed"
                        fill="#fee2e2"
                        radius={[4, 4, 0, 0]}
                        stroke="#ef4444"
                        strokeWidth={2}
                    />
                    <Bar
                        dataKey="pending"
                        name="Pending"
                        fill="#fef3c7"
                        radius={[4, 4, 0, 0]}
                        stroke="#f59e0b"
                        strokeWidth={2}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    // Campaign Filter Component
    const CampaignFilter = ({ campaigns, selectedCampaigns, onSelectionChange }) => {
        const [isOpen, setIsOpen] = useState(false);
        const dropdownRef = React.useRef(null);

        // Close dropdown when clicking outside
        React.useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        const handleCampaignToggle = (campaignId) => {
            if (campaignId === 'all') {
                onSelectionChange(['all']);
            } else {
                let newSelection = [...selectedCampaigns];

                // Remove 'all' if it exists
                newSelection = newSelection.filter(id => id !== 'all');

                if (newSelection.includes(campaignId)) {
                    newSelection = newSelection.filter(id => id !== campaignId);
                } else {
                    newSelection.push(campaignId);
                }

                // If no campaigns selected, default to 'all'
                if (newSelection.length === 0) {
                    newSelection = ['all'];
                }

                onSelectionChange(newSelection);
            }
        };

        const getSelectedText = () => {
            if (selectedCampaigns.includes('all')) {
                return 'All Campaigns';
            }
            if (selectedCampaigns.length === 1) {
                const campaign = campaigns.find(c => c._id === selectedCampaigns[0]);
                return campaign ? campaign.title : 'Select Campaigns';
            }
            return `${selectedCampaigns.length} Campaigns Selected`;
        };

        return (
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-soft-700 bg-white border border-soft-300 rounded-lg hover:bg-soft-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                    <span>{getSelectedText()}</span>
                    <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-soft-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2">
                            <label className="flex items-center p-2 hover:bg-soft-50 rounded cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedCampaigns.includes('all')}
                                    onChange={() => handleCampaignToggle('all')}
                                    className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-soft-300 rounded"
                                />
                                <span className="text-sm font-medium text-soft-900">All Campaigns</span>
                            </label>

                            <div className="border-t border-soft-200 my-2"></div>

                            {campaigns.map((campaign) => (
                                <label key={campaign._id} className="flex items-center p-2 hover:bg-soft-50 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedCampaigns.includes(campaign._id)}
                                        onChange={() => handleCampaignToggle(campaign._id)}
                                        className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-soft-300 rounded"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-soft-900 truncate">
                                            {campaign.title}
                                        </div>
                                        <div className="text-xs text-soft-500">
                                            {campaign.type} • {campaign.status}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Welcome section skeleton */}
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8 border border-primary-200/50 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="h-8 bg-primary-200 rounded-lg w-80 animate-pulse mb-3"></div>
                            <div className="h-5 bg-primary-100 rounded-lg w-96 animate-pulse mb-6"></div>
                            <div className="flex items-center space-x-6">
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-primary-200/50">
                                    <div className="h-5 w-5 bg-primary-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-primary-200 rounded w-16 animate-pulse"></div>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-secondary-200/50">
                                    <div className="h-5 w-5 bg-secondary-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-secondary-200 rounded w-20 animate-pulse"></div>
                                </div>
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-success-200/50">
                                    <div className="h-5 w-5 bg-success-200 rounded animate-pulse"></div>
                                    <div className="h-4 bg-success-200 rounded w-18 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                        <div className="ml-8">
                            <div className="h-14 bg-primary-200 rounded-xl w-56 animate-pulse"></div>
                        </div>
                    </div>
                </div>

                {/* Loading center piece */}
                <div className="text-center py-12">
                    <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
                        <div className="absolute inset-0 rounded-full border-4 border-primary-200 animate-spin border-t-primary-600"></div>
                        <div className="absolute inset-2 rounded-full border-2 border-secondary-100 animate-spin border-t-secondary-400" style={{ animationDirection: 'reverse', animationDuration: '2s' }}></div>
                        <div className="absolute inset-4 rounded-full border-2 border-primary-100 animate-pulse"></div>
                        <ChartBarIcon className="h-8 w-8 text-primary-600 animate-pulse" />
                    </div>
                    <h3 className="text-2xl font-bold text-soft-900 mb-3">Loading Your Dashboard</h3>
                    <p className="text-soft-600 text-lg mb-6">Analyzing your campaign performance and metrics...</p>

                    {/* Progress dots */}
                    <div className="flex items-center justify-center space-x-3 mb-8">
                        <div className="w-3 h-3 bg-primary-400 rounded-full animate-bounce"></div>
                        <div className="w-3 h-3 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-3 h-3 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-3 h-3 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                    </div>

                    {/* Fun loading message */}
                    <div className="inline-flex items-center space-x-3 bg-white/80 px-6 py-3 rounded-full border border-primary-200/50 shadow-sm">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-300 border-t-primary-600"></div>
                        <span className="text-soft-600 font-medium animate-pulse">Preparing your communication insights...</span>
                    </div>
                </div>

                {/* Stats cards skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="h-4 bg-soft-200 rounded w-20 animate-pulse mb-2"></div>
                                    <div className="h-8 bg-soft-100 rounded w-12 animate-pulse mb-2"></div>
                                    <div className="flex items-center">
                                        <div className="h-4 w-4 bg-soft-200 rounded animate-pulse mr-1"></div>
                                        <div className="h-3 bg-soft-200 rounded w-16 animate-pulse"></div>
                                    </div>
                                </div>
                                <div className="h-16 w-16 bg-soft-100 rounded-xl animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Performance metrics skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="h-6 bg-soft-200 rounded w-48 animate-pulse mb-2"></div>
                        <div className="h-4 bg-soft-100 rounded w-64 animate-pulse"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="text-center p-5 rounded-xl bg-soft-50 border border-soft-200/50">
                                <div className="h-8 bg-soft-200 rounded w-16 mx-auto animate-pulse mb-2"></div>
                                <div className="h-4 bg-soft-100 rounded w-20 mx-auto animate-pulse"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Chart sections skeleton */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    {[...Array(2)].map((_, index) => (
                        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                            <div className="mb-6">
                                <div className="h-6 bg-soft-200 rounded w-40 animate-pulse mb-2"></div>
                                <div className="h-4 bg-soft-100 rounded w-56 animate-pulse"></div>
                            </div>
                            <div className="h-64 bg-soft-50 rounded-lg animate-pulse"></div>
                        </div>
                    ))}
                </div>

                {/* Recent campaigns skeleton */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <div className="h-6 bg-soft-200 rounded w-36 animate-pulse mb-2"></div>
                        <div className="h-4 bg-soft-100 rounded w-48 animate-pulse"></div>
                    </div>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="flex items-center justify-between p-4 bg-soft-50 rounded-lg">
                                <div className="flex items-center space-x-4">
                                    <div className="h-10 w-10 bg-soft-200 rounded-lg animate-pulse"></div>
                                    <div>
                                        <div className="h-4 bg-soft-200 rounded w-32 animate-pulse mb-1"></div>
                                        <div className="h-3 bg-soft-100 rounded w-24 animate-pulse"></div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="h-6 bg-soft-200 rounded-full w-16 animate-pulse"></div>
                                    <div className="h-8 w-8 bg-soft-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="text-center">
                    <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={fetchDashboardStats}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4 sm:p-6 lg:p-8 border border-primary-200/50 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-soft-900">
                            Welcome back, {user?.name || 'User'}! 👋
                        </h1>
                        <p className="text-soft-700 text-base sm:text-lg leading-relaxed mb-6 lg:mb-0">
                            {isAdmin()
                                ? 'Manage campaigns and monitor system activity from your dashboard'
                                : 'Create and track your communication campaigns with ease'}
                        </p>
                        <div className="mt-4 lg:mt-6 flex flex-col sm:flex-row sm:flex-wrap lg:flex-row lg:items-center gap-3 sm:gap-4 lg:gap-6 text-soft-600">
                            <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-primary-200/50 flex-shrink-0">
                                <UserGroupIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm truncate">Role: {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || 'User'}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-secondary-200/50 flex-shrink-0">
                                <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-600 flex-shrink-0" />
                                <span className="font-medium text-xs sm:text-sm truncate">Total Campaigns: {stats?.campaigns?.total || 0}</span>
                            </div>
                            {stats?.performance?.successRate && (
                                <div className="flex items-center space-x-2 bg-white/60 px-3 py-2 rounded-lg border border-success-200/50 flex-shrink-0">
                                    <SignalIcon className="h-4 w-4 sm:h-5 sm:w-5 text-success-600 flex-shrink-0" />
                                    <span className="font-medium text-xs sm:text-sm truncate">Success Rate: {stats.performance.successRate}%</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {!isAdmin() && (
                        <div className="lg:ml-8 flex-shrink-0">
                            <Link
                                to="/campaigns/create"
                                className="inline-flex items-center justify-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg border border-primary-300 w-full sm:w-auto"
                            >
                                <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                                <span className="text-sm sm:text-base">Create New Campaign</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Campaign Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-soft-600 text-sm font-medium mb-2">Total Campaigns</p>
                            <p className="text-2xl sm:text-3xl font-bold text-soft-900 mb-2">{stats?.campaigns?.total || 0}</p>
                            <div className="flex items-center text-success-600 text-sm font-medium">
                                <ChartBarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate">All time</span>
                            </div>
                        </div>
                        <div className="h-12 w-12 sm:h-16 sm:w-16 bg-primary-100 rounded-xl flex items-center justify-center border border-primary-200/50 ml-3 flex-shrink-0">
                            <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-soft-600 text-sm font-medium mb-2">Pending</p>
                            <p className="text-2xl sm:text-3xl font-bold text-soft-900 mb-2">{stats?.campaigns?.pending || 0}</p>
                            <div className="flex items-center text-warning-600 text-sm font-medium">
                                <ClockIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate">Awaiting action</span>
                            </div>
                        </div>
                        <div className="h-12 w-12 sm:h-16 sm:w-16 bg-warning-100 rounded-xl flex items-center justify-center border border-warning-200/50 ml-3 flex-shrink-0">
                            <ClockIcon className="h-6 w-6 sm:h-8 sm:w-8 text-warning-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-soft-600 text-sm font-medium mb-2">Ongoing</p>
                            <p className="text-2xl sm:text-3xl font-bold text-soft-900 mb-2">{stats?.campaigns?.ongoing || 0}</p>
                            <div className="flex items-center text-secondary-600 text-sm font-medium">
                                <BoltIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate">In progress</span>
                            </div>
                        </div>
                        <div className="h-12 w-12 sm:h-16 sm:w-16 bg-secondary-100 rounded-xl flex items-center justify-center border border-secondary-200/50 ml-3 flex-shrink-0">
                            <BoltIcon className="h-6 w-6 sm:h-8 sm:w-8 text-secondary-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-soft-600 text-sm font-medium mb-2">Completed</p>
                            <p className="text-2xl sm:text-3xl font-bold text-soft-900 mb-2">{stats?.campaigns?.completed || 0}</p>
                            <div className="flex items-center text-success-600 text-sm font-medium">
                                <CheckCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate">Successfully done</span>
                            </div>
                        </div>
                        <div className="h-12 w-12 sm:h-16 sm:w-16 bg-success-100 rounded-xl flex items-center justify-center border border-success-200/50 ml-3 flex-shrink-0">
                            <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-success-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-soft-600 text-sm font-medium mb-2">Paused</p>
                            <p className="text-2xl sm:text-3xl font-bold text-soft-900 mb-2">{stats?.campaigns?.paused || 0}</p>
                            <div className="flex items-center text-error-600 text-sm font-medium">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                <span className="truncate">Suspended</span>
                            </div>
                        </div>
                        <div className="h-12 w-12 sm:h-16 sm:w-16 bg-error-100 rounded-xl flex items-center justify-center border border-error-200/50 ml-3 flex-shrink-0">
                            <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-error-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* User Credits Information */}
            {stats?.userCredits && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 shadow-sm border border-green-200/50">
                    <div className="mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 mr-2 flex-shrink-0" />
                            <span className="truncate">Your Credit Balance</span>
                        </h3>
                        <p className="text-soft-600 text-sm sm:text-base">Available credits for your campaigns</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-xl sm:text-3xl font-bold text-blue-700 mb-1 sm:mb-2">
                                {stats.userCredits.sms?.toLocaleString() || 0}
                            </div>
                            <div className="text-xs sm:text-sm text-blue-600 font-medium">SMS Credits</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-xl sm:text-3xl font-bold text-green-700 mb-1 sm:mb-2">
                                {stats.userCredits.whatsapp?.toLocaleString() || 0}
                            </div>
                            <div className="text-xs sm:text-sm text-green-600 font-medium">WhatsApp Credits</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-xl sm:text-3xl font-bold text-yellow-700 mb-1 sm:mb-2">
                                {stats.userCredits.voice?.toLocaleString() || 0}
                            </div>
                            <div className="text-xs sm:text-sm text-yellow-600 font-medium">Voice Credits</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-xl sm:text-3xl font-bold text-soft-900 mb-1 sm:mb-2">
                                {stats.userCredits.total?.toLocaleString() || 0}
                            </div>
                            <div className="text-xs sm:text-sm text-soft-600 font-medium">Total Credits</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Performance Metrics */}
            {stats?.performance && (
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2">Performance Overview</h3>
                        <p className="text-soft-600 text-sm sm:text-base">Key metrics for your campaign performance</p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-soft-50 border border-soft-200/50">
                            <div className="text-xl sm:text-3xl font-bold text-soft-900 mb-1 sm:mb-2">
                                {stats.performance.totalReach?.toLocaleString() || 0}
                            </div>
                            <div className="text-xs sm:text-sm text-soft-600 font-medium">Total Reach</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-success-50 border border-success-200/50">
                            <div className="text-xl sm:text-3xl font-bold text-success-700 mb-1 sm:mb-2">
                                {stats.performance.successRate || 0}%
                            </div>
                            <div className="text-xs sm:text-sm text-success-600 font-medium">Success Rate</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-primary-50 border border-primary-200/50">
                            <div className="text-xl sm:text-3xl font-bold text-primary-700 mb-1 sm:mb-2">
                                {stats.performance.deliveryRate || 0}%
                            </div>
                            <div className="text-xs sm:text-sm text-primary-600 font-medium">Delivery Rate</div>
                        </div>
                        <div className="text-center p-3 sm:p-5 rounded-xl bg-error-50 border border-error-200/50">
                            <div className="text-xl sm:text-3xl font-bold text-error-700 mb-1 sm:mb-2">
                                {stats.performance.failureRate || 0}%
                            </div>
                            <div className="text-xs sm:text-sm text-error-600 font-medium">Failure Rate</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Voice Campaign Listening Statistics */}
            {(stats?.listeningStatistics || voiceCampaignStats) && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 sm:p-6 shadow-sm border border-purple-200/50">
                    <div className="mb-4 sm:mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-4 lg:space-y-0">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex flex-wrap items-center">
                                    <SpeakerWaveIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 mr-2 flex-shrink-0" />
                                    <span className="truncate">Voice Campaign Listening Statistics</span>
                                    {voiceCampaignStats?.campaignTitle && (
                                        <span className="ml-2 text-xs sm:text-sm font-normal text-purple-600 bg-purple-100 px-2 py-1 rounded-full mt-1 lg:mt-0">
                                            {voiceCampaignStats.campaignTitle}
                                        </span>
                                    )}
                                </h3>
                                <p className="text-soft-600 text-sm sm:text-base">
                                    Analysis of how much of your voice campaigns people listened to
                                    <span className="block sm:inline">
                                        ({(voiceCampaignStats || stats.listeningStatistics).totalVoiceCampaigns} campaigns • {(voiceCampaignStats || stats.listeningStatistics).totalVoiceContacts?.toLocaleString()} contacts)
                                    </span>
                                </p>
                            </div>
                            {/* Campaign Filter */}
                            {stats?.availableVoiceCampaigns && stats.availableVoiceCampaigns.length > 0 && (
                                <div className="w-full lg:w-64 flex-shrink-0">
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Filter by Campaign:
                                    </label>
                                    <select
                                        value={selectedVoiceCampaign}
                                        onChange={(e) => setSelectedVoiceCampaign(e.target.value)}
                                        className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-soft-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                    >
                                        <option value="all">All Voice Campaigns</option>
                                        {stats.availableVoiceCampaigns.map((campaign) => (
                                            <option key={campaign._id} value={campaign._id}>
                                                {campaign.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Engagement Overview Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
                        <div className="text-center p-4 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl sm:text-3xl font-bold text-green-700 mb-1 sm:mb-2">
                                {(voiceCampaignStats || stats.listeningStatistics).highEngagement}%
                            </div>
                            <div className="text-xs sm:text-sm text-green-600 font-medium mb-1">High Engagement</div>
                            <div className="text-xs text-green-500">Pulse 1-4 sec</div>
                        </div>
                        <div className="text-center p-4 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl sm:text-3xl font-bold text-yellow-700 mb-1 sm:mb-2">
                                {(voiceCampaignStats || stats.listeningStatistics).mediumEngagement}%
                            </div>
                            <div className="text-xs sm:text-sm text-yellow-600 font-medium mb-1">Medium Engagement</div>
                            <div className="text-xs text-yellow-500">Pulse 5-6 sec</div>
                        </div>
                        <div className="text-center p-4 sm:p-5 rounded-xl bg-white/70 border border-white/50 shadow-sm">
                            <div className="text-2xl sm:text-3xl font-bold text-red-700 mb-1 sm:mb-2">
                                {(voiceCampaignStats || stats.listeningStatistics).lowEngagement}%
                            </div>
                            <div className="text-xs sm:text-sm text-red-600 font-medium mb-1">Low Engagement</div>
                            <div className="text-xs text-red-500">Pulse 7-10 sec</div>
                        </div>
                    </div>

                    {/* Detailed Listening Breakdown */}
                    <div className="bg-white/50 rounded-xl p-4 sm:p-6 border border-white/50">
                        <h4 className="text-base sm:text-lg font-semibold text-soft-900 mb-4">Detailed Listening Breakdown</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                            <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b from-green-100 to-green-50 border border-green-200">
                                <div className="text-lg sm:text-2xl font-bold text-green-700 mb-1">
                                    {(voiceCampaignStats || stats.listeningStatistics).averageListeningStats.heard100Percent}%
                                </div>
                                <div className="text-xs text-green-600 font-medium">Pulse 1-2 sec</div>
                            </div>
                            <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b from-blue-100 to-blue-50 border border-blue-200">
                                <div className="text-lg sm:text-2xl font-bold text-blue-700 mb-1">
                                    {(voiceCampaignStats || stats.listeningStatistics).averageListeningStats.heard80to99Percent}%
                                </div>
                                <div className="text-xs text-blue-600 font-medium">Pulse 3-4 sec</div>
                            </div>
                            <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b from-yellow-100 to-yellow-50 border border-yellow-200">
                                <div className="text-lg sm:text-2xl font-bold text-yellow-700 mb-1">
                                    {(voiceCampaignStats || stats.listeningStatistics).averageListeningStats.heard50to79Percent}%
                                </div>
                                <div className="text-xs text-yellow-600 font-medium">Pulse 5-6 sec</div>
                            </div>
                            <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b from-orange-100 to-orange-50 border border-orange-200">
                                <div className="text-lg sm:text-2xl font-bold text-orange-700 mb-1">
                                    {(voiceCampaignStats || stats.listeningStatistics).averageListeningStats.heard30to49Percent}%
                                </div>
                                <div className="text-xs text-orange-600 font-medium">Pulse 7-8 sec</div>
                            </div>
                            <div className="text-center p-3 sm:p-4 rounded-lg bg-gradient-to-b from-red-100 to-red-50 border border-red-200">
                                <div className="text-lg sm:text-2xl font-bold text-red-700 mb-1">
                                    {(voiceCampaignStats || stats.listeningStatistics).averageListeningStats.heard1to29Percent}%
                                </div>
                                <div className="text-xs text-red-600 font-medium">Pulse 9-10 sec</div>
                            </div>
                        </div>

                        {/* Insights */}
                        <div className="mt-6 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                            <div className="flex items-start space-x-3">
                                <LightBulbIcon className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <h5 className="font-semibold text-indigo-900 mb-1">Voice Campaign Insights</h5>
                                    <p className="text-sm text-indigo-700">
                                        {(voiceCampaignStats || stats.listeningStatistics).highEngagement >= 60
                                            ? `Excellent! ${(voiceCampaignStats || stats.listeningStatistics).highEngagement}% of your audience shows high engagement with your voice campaigns.`
                                            : (voiceCampaignStats || stats.listeningStatistics).highEngagement >= 40
                                                ? `Good engagement at ${(voiceCampaignStats || stats.listeningStatistics).highEngagement}%. Consider optimizing call content to increase full listening rates.`
                                                : `Low engagement at ${(voiceCampaignStats || stats.listeningStatistics).highEngagement}%. Consider shortening calls or improving opening hooks to retain listener attention.`
                                        }
                                        {(voiceCampaignStats || stats.listeningStatistics).lowEngagement > 30 &&
                                            ` ${(voiceCampaignStats || stats.listeningStatistics).lowEngagement}% of listeners disconnect early - review your message delivery and timing.`
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Campaign Filter Chart */}
            {allCampaigns.length > 0 && (
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-4 sm:mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-4 lg:space-y-0">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg sm:text-xl font-bold text-soft-900 mb-2 flex items-center">
                                    <FunnelIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 mr-2 flex-shrink-0" />
                                    <span className="truncate">Campaign Performance Analysis</span>
                                </h3>
                                <p className="text-soft-600 text-sm sm:text-base">Detailed statistics for selected campaigns</p>
                            </div>
                            <div className="w-full lg:w-64 flex-shrink-0">
                                <CampaignFilter
                                    campaigns={allCampaigns}
                                    selectedCampaigns={selectedCampaigns}
                                    onSelectionChange={setSelectedCampaigns}
                                />
                            </div>
                        </div>

                        {/* Summary Cards */}
                        {campaignFilterData.length > 0 && (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 rounded-lg border border-blue-200">
                                    <div className="text-lg sm:text-2xl font-bold text-blue-700">
                                        {campaignFilterData.reduce((sum, campaign) => sum + campaign.totalContacts, 0).toLocaleString()}
                                    </div>
                                    <div className="text-xs sm:text-sm text-blue-600 font-medium">Total Contacts</div>
                                </div>
                                <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 sm:p-4 rounded-lg border border-green-200">
                                    <div className="text-lg sm:text-2xl font-bold text-green-700">
                                        {campaignFilterData.reduce((sum, campaign) => sum + campaign.delivered, 0).toLocaleString()}
                                    </div>
                                    <div className="text-xs sm:text-sm text-green-600 font-medium">Delivered</div>
                                </div>
                                <div className="bg-gradient-to-r from-red-50 to-red-100 p-3 sm:p-4 rounded-lg border border-red-200">
                                    <div className="text-lg sm:text-2xl font-bold text-red-700">
                                        {campaignFilterData.reduce((sum, campaign) => sum + campaign.failed, 0).toLocaleString()}
                                    </div>
                                    <div className="text-xs sm:text-sm text-red-600 font-medium">Failed</div>
                                </div>
                                <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-3 sm:p-4 rounded-lg border border-yellow-200">
                                    <div className="text-lg sm:text-2xl font-bold text-yellow-700">
                                        {campaignFilterData.reduce((sum, campaign) => sum + campaign.pending, 0).toLocaleString()}
                                    </div>
                                    <div className="text-xs sm:text-sm text-yellow-600 font-medium">Pending</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {campaignFilterData.length > 0 ? (
                        <div className="h-96">
                            <CampaignFilterChart data={campaignFilterData} />
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FunnelIcon className="h-16 w-16 text-soft-400 mx-auto mb-4" />
                            <h4 className="text-lg font-semibold text-soft-900 mb-2">No Campaign Data</h4>
                            <p className="text-soft-600">Select campaigns to view their performance statistics</p>
                        </div>
                    )}
                </div>
            )}

            {/* Monthly Trends */}
            {stats?.monthlyTrends && stats.monthlyTrends.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <ArrowTrendingUpIcon className="h-6 w-6 text-success-600 mr-2" />
                            Monthly Campaign Performance
                        </h3>
                        <p className="text-soft-600">Campaign activity and delivery performance over the last 6 months</p>
                    </div>
                    <div className="h-80">
                        <MonthlyTrendsChart data={stats.monthlyTrends} formatMonth={formatMonth} />
                    </div>
                </div>
            )}

            {/* Performance Insights */}
            {stats?.insights && stats.insights.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                            <SparklesIcon className="h-6 w-6 text-secondary-600 mr-2" />
                            Performance Insights
                        </h3>
                        <p className="text-soft-600">AI-powered analysis and recommendations for your campaigns</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {stats.insights.map((insight, index) => {
                            const Icon = getInsightIcon(insight.type);
                            return (
                                <div
                                    key={index}
                                    className={`p-4 rounded-xl border-2 ${getInsightColorClass(insight.type)}`}
                                >
                                    <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0">
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-lg mb-1">{insight.title}</h4>
                                            <p className="text-sm opacity-90">{insight.message}</p>
                                        </div>
                                        {insight.metric && (
                                            <div className="flex-shrink-0 text-right">
                                                <div className="text-2xl font-bold">{insight.metric}%</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Performance Analysis Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Channel Performance */}
                {stats?.channelPerformance && stats.channelPerformance.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                                <ChartPieIcon className="h-6 w-6 text-primary-600 mr-2" />
                                Channel Performance Comparison
                            </h3>
                            <p className="text-soft-600">Success rates across different communication channels</p>
                        </div>
                        <div className="h-64">
                            <ChannelPerformanceChart data={stats.channelPerformance} />
                        </div>
                    </div>
                )}

                {/* Recent Campaigns */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-soft-200/60">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-soft-900 mb-2 flex items-center">
                                <CalendarDaysIcon className="h-6 w-6 text-secondary-600 mr-2" />
                                Recent Campaigns
                            </h3>
                            <p className="text-soft-600">Your latest campaign activities</p>
                        </div>
                        <Link
                            to="/campaigns"
                            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm bg-primary-50 hover:bg-primary-100 px-4 py-2 rounded-lg transition-colors duration-200 border border-primary-200/50"
                        >
                            View All →
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {stats?.recentCampaigns?.length > 0 ? (
                            stats.recentCampaigns.map((campaign) => (
                                <div key={campaign._id} className="flex items-center justify-between p-4 rounded-xl bg-soft-50 hover:bg-soft-100 transition-colors border border-soft-200/50">
                                    <div className="flex items-center space-x-4">
                                        <div className="h-12 w-12 bg-soft-200 rounded-xl flex items-center justify-center border border-soft-300/50">
                                            {React.createElement(campaignTypeIcons[campaign.type] || DocumentTextIcon, {
                                                className: "h-6 w-6 text-soft-600"
                                            })}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-soft-900">{campaign.title}</p>
                                            <p className="text-sm text-soft-600">
                                                {campaign.type.toUpperCase()} • {campaign.statistics?.totalNumbers || 0} numbers
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${campaign.status === 'Completed' ? 'bg-success-100 text-success-700 border border-success-200/50' :
                                            campaign.status === 'Ongoing' ? 'bg-secondary-100 text-secondary-700 border border-secondary-200/50' :
                                                campaign.status === 'Pending' ? 'bg-warning-100 text-warning-700 border border-warning-200/50' :
                                                    'bg-error-100 text-error-700 border border-error-200/50'
                                            }`}>
                                            {campaign.status}
                                        </span>
                                        <p className="text-xs text-soft-500 mt-1">
                                            {new Date(campaign.createdAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12">
                                <div className="h-16 w-16 bg-soft-200 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-soft-300/50">
                                    <DocumentTextIcon className="h-8 w-8 text-soft-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-soft-900 mb-2">No campaigns yet</h4>
                                <p className="text-soft-600 mb-4">Get started by creating your first campaign</p>
                                {!isAdmin() && (
                                    <Link
                                        to="/campaigns/create"
                                        className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-colors duration-200 shadow-sm hover:shadow-md"
                                    >
                                        <PlusIcon className="h-5 w-5" />
                                        <span>Create Campaign</span>
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>


        </div>
    );
};

export default Dashboard; 